import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import {
    GoogleAdWordsLinkedService,
    googleAdWordsAuthTypes,
    apiTypes
} from 'app/model/linkedservice/google-adwords.linkedservice';
import {
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { Constants } from '../../../shared/shared-constants';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { googleAdwordsReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';
import { ValueAdapter } from '../value.adapter';
import { ModelMarker } from 'shell-models-lib';
import { driverVersions } from 'app/registries/consts/tabular-consts';

/**
 * accepted JSON payload
 *
 * GoogleAdwords (Legacy)
 * typeProperties: {
 *   ...
 * }
 *
 * GoogleAds (current)
 * typeProperties: {
 *   connectionProperties: {
 *     ...
 *   }
 * }
 *
 * Simba replacement (ff)
 * Recommended
 * typeProperties: {
 *   ...,
 *   supportLegacyDataTypes: true/false,
 *   googleAdsApiVersion: 'v#'
 * }
 * Legacy
 * typeProperties: {
 *   connectionProperties: {
 *     ...,
 *     supportLegacyDataTypes: true/false
 *   }
 * }
 */
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(GoogleAdWordsLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                clientCustomerID: '*',
                developerToken: 'secret',
                authenticationType: '*',
                clientId: '*',
                clientSecret: 'secret',
                refreshToken: 'secret',
                email: '*',
                loginCustomerID: '*',
                keyFilePath: '*', // deprecated in api v14
                trustedCertPath: '*', // deprecated in api v14
                useSystemTrustStore: '*', // deprecated in api v14
                encryptedCredential: '*',
                connectionProperties: {
                    clientCustomerID: '*',
                    loginCustomerID: '*',
                    developerToken: 'secret',
                    authenticationType: '*',
                    clientId: '*',
                    clientSecret: 'secret',
                    refreshToken: 'secret',
                    email: '*',
                    keyFilePath: '*',
                    trustedCertPath: '*',
                    useSystemTrustStore: '*',
                    encryptedCredential: '*',
                    supportLegacyDataTypes: '*'
                },
                privateKey: 'secret', // replaces keyFilePath
                googleAdsApiVersion: '*',
                supportLegacyDataTypes: '*'
            },
            parameters: '*'
        }
    }
})
export class GoogleAdWordsLinkedServiceAdapter extends LinkedServiceAdapter<GoogleAdWordsLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => GoogleAdWordsLinkedService {
        return GoogleAdWordsLinkedService;
    }

    protected model(): IModel {
        return GoogleAdWordsLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: GoogleAdWordsLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.GoogleAdWordsLinkedService;
        properties.type = Metamodel.modelFor(GoogleAdWordsLinkedService).name;
        let clientId: string;
        let clientSecret: string | Model.SecretBase;
        let refreshToken: string | Model.SecretBase;
        let email: string;
        let keyFilePath: string;
        let privateKey: string | Model.SecretBase;
        let trustedCertPath: string;
        let useSystemTrustStore: boolean;
        if (linkedService.authenticationType === googleAdWordsAuthTypes.UserAuthentication) {
            clientId = this.valueToResource(linkedService, GoogleAdWordsLinkedService.CLIENT_ID);
            clientSecret = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                GoogleAdWordsLinkedService.CLIENT_SECRET,
                this.context
            );
            refreshToken = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                GoogleAdWordsLinkedService.REFRESH_TOKEN,
                this.context
            );
        } else if (linkedService.authenticationType === googleAdWordsAuthTypes.ServiceAuthentication) {
            email = this.valueToResource(linkedService, GoogleAdWordsLinkedService.EMAIL) || undefined;
            // with simba replacement support, `privateKey` replaces `keyFilePath` & is a sensitive field
            // `useSystemTrustStore` and `trustedCertPath` no longer exists
            if (linkedService.driverVersion === driverVersions.Recommended) {
                privateKey = parameterizablePasswordOrAKVtoSecretBase(
                    linkedService,
                    GoogleAdWordsLinkedService.PRIVATE_KEY,
                    this.context
                );
            } else {
                keyFilePath =
                    this.valueToResource(linkedService, GoogleAdWordsLinkedService.KEY_FILE_PATH) || undefined;
                trustedCertPath =
                    this.valueToResource(linkedService, GoogleAdWordsLinkedService.TRUSTED_CERT_PATH) || undefined;
                useSystemTrustStore = linkedService.useSystemTrustStore;
            }
        }

        const clientCustomerID =
            this.valueToResource(linkedService, GoogleAdWordsLinkedService.CLIENT_CUSTOM_ID) || undefined;
        const loginCustomerID =
            linkedService.apiType === apiTypes.GoogleAds
                ? this.valueToResource(linkedService, GoogleAdWordsLinkedService.LOGIN_CUSTOMER_ID) || undefined
                : undefined;
        const developerToken = parameterizablePasswordOrAKVtoSecretBase(
            linkedService,
            GoogleAdWordsLinkedService.DEVELOPER_TOKEN,
            this.context
        );
        const lsProperties = {
            clientCustomerID,
            loginCustomerID,
            developerToken,
            authenticationType: linkedService.authenticationType,
            clientId,
            clientSecret,
            refreshToken,
            email,
            privateKey,
            keyFilePath,
            trustedCertPath,
            useSystemTrustStore
        };

        if (linkedService.apiType === apiTypes.GoogleAds) {
            properties.typeProperties = {
                connectionProperties: lsProperties
            };

            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        } else { // GoogleAdWords
            properties.typeProperties = lsProperties;
        }

        // for all newly created, `supportLegacyDataTypes` is `false` per-model definition
        // for all existing that'll be upgraded, `supportLegacyDataTypes` will be `true` as it's currently undefined
        const supportLegacyDataTypes = ModelMarker.isNew(linkedService)
            ? false
            : linkedService.supportLegacyDataTypes != null
                ? linkedService.supportLegacyDataTypes
                : true;
        if (linkedService.driverVersion === driverVersions.Recommended) {
            if (linkedService.apiType === apiTypes.GoogleAds) {
                // needs to convert legacy code to new version for simba support
                const connectionProperties = properties.typeProperties.connectionProperties;
                properties.typeProperties = {
                    ...connectionProperties,
                    supportLegacyDataTypes,
                    googleAdsApiVersion: linkedService.googleAdsApiVersion
                };
            }
            // GoogleAdWords update no longer supported, thus not handled here
        } else if (properties.typeProperties.connectionProperties) { // GoogleAds
            properties.typeProperties.connectionProperties.supportLegacyDataTypes = supportLegacyDataTypes;
        } else { // GoogleAdWords
            properties.typeProperties.supportLegacyDataTypes = supportLegacyDataTypes;
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: GoogleAdWordsLinkedService
    ): Promise<GoogleAdWordsLinkedService> {
        const properties = linkedServiceResource.properties as Model.GoogleAdWordsLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.GoogleAdWordsLinkedServiceTypeProperties = properties.typeProperties;

        let lsProperties;
        let hasDependency: boolean = true;

        /**
         * JSON structure revoluton:
         *  I (isOldResource)  - typeProperties: {...}
         *  II (current)       - typeProperties: { connectionProperties: {...} }
         *  III (Simba Replacement) Rec    - typeProperties: { ..., supportLegacyDataTypes, googleAdsApiVersion }
         *  III (Simba Replacement) Legacy - typeProperties: { connectionProperties: {..., supportLegacyDataTypes} }
         */
        if (typeProperties.connectionProperties != null || typeProperties.supportLegacyDataTypes != null) {
            linkedService.isOldResource = false;
            linkedService.apiType = apiTypes.GoogleAds;
            lsProperties = typeProperties.connectionProperties ?? typeProperties;
            linkedService.loginCustomerID = lsProperties.loginCustomerID;
            hasDependency = lsProperties.encryptedCredential != null;
            linkedService.additionalFields = [];
            const connectionProperties = lsProperties;
            Object.keys(connectionProperties).forEach((key) => {
                if (googleAdwordsReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: connectionProperties[key] });
                    delete connectionProperties[key];
                }
            });
        } else {
            linkedService.isOldResource = true;
            linkedService.apiType = apiTypes.GoogleAdWords;
            lsProperties = typeProperties;
            hasDependency =
                lsProperties.encryptedCredential != null ||
                (lsProperties.connectionProperties
                    ? lsProperties.connectionProperties.encryptedCredential != null
                    : false);
        }

        ValueAdapter.toModel(linkedService, GoogleAdWordsLinkedService.CLIENT_CUSTOM_ID, lsProperties.clientCustomerID);
        ValueAdapter.toModel(
            linkedService,
            GoogleAdWordsLinkedService.CLIENT_ID,
            lsProperties.authenticationType === googleAdWordsAuthTypes.UserAuthentication
                ? lsProperties.clientId || Constants.maskedCredential
                : undefined
        );
        ValueAdapter.toModel(linkedService, GoogleAdWordsLinkedService.LOGIN_CUSTOMER_ID, lsProperties.loginCustomerID);
        ValueAdapter.toModel(linkedService, GoogleAdWordsLinkedService.EMAIL, lsProperties.email);
        ValueAdapter.toModel(linkedService, GoogleAdWordsLinkedService.KEY_FILE_PATH, lsProperties.keyFilePath);
        ValueAdapter.toModel(linkedService, GoogleAdWordsLinkedService.TRUSTED_CERT_PATH, lsProperties.trustedCertPath);
        parameterizableSecretBaseToModel(
            linkedService,
            GoogleAdWordsLinkedService.DEVELOPER_TOKEN,
            lsProperties.developerToken, this.context,
            hasDependency
        );
        parameterizableSecretBaseToModel(
            linkedService,
            GoogleAdWordsLinkedService.CLIENT_SECRET,
            lsProperties.clientSecret, this.context,
            lsProperties.authenticationType === googleAdWordsAuthTypes.UserAuthentication
        );
        parameterizableSecretBaseToModel(
            linkedService,
            GoogleAdWordsLinkedService.REFRESH_TOKEN,
            lsProperties.refreshToken, this.context,
            lsProperties.authenticationType === googleAdWordsAuthTypes.UserAuthentication
        );

        linkedService.supportLegacyDataTypes = lsProperties.supportLegacyDataTypes;
        linkedService.driverVersion = lsProperties.googleAdsApiVersion
            ? driverVersions.Recommended
            : driverVersions.Legacy;
        linkedService.googleAdsApiVersion = lsProperties.googleAdsApiVersion;
        linkedService.isLegacyConnector = linkedService.driverVersion === driverVersions.Legacy;

        parameterizableSecretBaseToModel(
            linkedService,
            GoogleAdWordsLinkedService.PRIVATE_KEY,
            lsProperties.privateKey, this.context,
            lsProperties.privateKey
        );

        linkedService.authenticationType = lsProperties.authenticationType;
        linkedService.useSystemTrustStore = lsProperties.useSystemTrustStore;

        return linkedService;
    }
}
